import { useCallback, useEffect, useReducer } from 'react'
import { toast } from 'react-toastify'

import { notepadsService } from '../services/http/notepads.service'
import { Notepad } from '../services/models/Notepad.model'
import { useHttp } from './useHttp'

// Interface defining the hook's return value and available methods
export interface NotepadHookState {
    notepad: Notepad | null
    createNotepad(userId: string, visitId: string, content: string): Promise<Notepad | null>
    updateNotepad(id: string, updates: Partial<Notepad>): Promise<void>
    handleNotepadUpdate(userId: string, visitId: string, content: string): Promise<void>
}

// Action types for the reducer
enum ACTION {
    SET
}

// Action interface for the reducer
interface Action {
    type: ACTION
    payload: string | Notepad | Notepad[] | null
}

// State interface for the reducer
interface State {
    notepad: Notepad | null
}

// Initial state for the reducer
const initialState: State = {
    notepad: null
}

// Reducer function to handle state updates
const reducer = (state: State, { type, payload }: Action): State => {
    switch (type) {
        case ACTION.SET: {
            const notepad = payload as Notepad
            return { ...state, notepad }
        }

        default:
            return state
    }
}

// Custom hook for managing notepad state and operations
export const useNotepad = (visitId: string | undefined): NotepadHookState => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const { notepad } = state
    const { getNotepadReq, createNotepadReq, updateNotepadReq } = useHttpReq()

    // Fetches notepad for a specific visit
    const getNotepad = useCallback(async () => {
        if (!visitId) {
            return Promise.resolve(null)
        }
        return getNotepadReq(visitId)
            .then((_notepad) => {
                dispatch({ type: ACTION.SET, payload: _notepad })
                return _notepad
            })
            .catch((error) => {
                console.error(error)
                return null
            })
    }, [visitId, getNotepadReq])

    // Creates a new notepad with content
    const createNotepad = useCallback(
        async (userId: string, visitId: string, content: string) => {
            if (!content) {
                return null
            }
            return createNotepadReq(userId, visitId, content)
                .then((_notepad) => {
                    dispatch({ type: ACTION.SET, payload: _notepad })
                    return _notepad
                })
                .catch((error) => {
                    console.error(error)
                    toast.error('Error creating notepad')
                    return null
                })
        },
        [createNotepadReq]
    )

    // Updates an existing notepad
    const updateNotepad = useCallback(
        async (id: string, updates: Partial<Notepad>) => {
            return updateNotepadReq(id, updates)
                .then(() => {
                    if (notepad) {
                        const updatedNotepad: Notepad = {
                            ...notepad,
                            ...updates
                        }
                        dispatch({ type: ACTION.SET, payload: updatedNotepad })
                    }
                })
                .catch((error) => {
                    console.error(error)
                    toast.error('Error updating notepad')
                })
        },
        [notepad, updateNotepadReq]
    )

    // Handles create or update based on whether notepad exists
    const handleNotepadUpdate = useCallback(
        async (userId: string, visitId: string, content: string) => {
            if (notepad?._id) {
                await updateNotepad(notepad._id, { content })
            } else {
                await createNotepad(userId, visitId, content)
            }
        },
        [notepad, createNotepad, updateNotepad]
    )

    // Fetch notepad on initial load
    useEffect(() => {
        getNotepad()
    }, [getNotepad])

    return {
        notepad,
        createNotepad,
        updateNotepad,
        handleNotepadUpdate
    }
}

// Helper hook for HTTP operations
const useHttpReq = () => {
    const { sendRequest } = useHttp()

    // Request to get a notepad by visit ID
    const getNotepadReq = useCallback(async (visitId: string): Promise<Notepad | null> => sendRequest(notepadsService.getNotepad.bind({}, visitId)), [sendRequest])

    // Request to create a new notepad
    const createNotepadReq = useCallback(
        async (userId: string, visitId: string, content: string): Promise<Notepad | null> => sendRequest(notepadsService.createNotepad.bind({}, userId, visitId, content)),
        [sendRequest]
    )

    // Request to update an existing notepad
    const updateNotepadReq = useCallback(
        async (id: string, updates: Partial<Notepad>): Promise<void> => sendRequest(notepadsService.updateNotepad.bind({}, id, updates)),
        [sendRequest]
    )

    return {
        getNotepadReq,
        createNotepadReq,
        updateNotepadReq
    }
}
